import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { TID } from "../lib/testIds";

const SERVICES = [
  {
    key: "apps",
    eyebrow: "01 — Engineering",
    title: "Web, Mobile & Desktop Apps",
    body: "Production-grade product engineering with React, Next.js, React Native, Swift, Kotlin and Electron. Pixel-precise UI, type-safe APIs, observability from day one.",
    tags: ["React / Next", "iOS · Android", "Electron · Tauri", "Type-safe APIs"],
    span: "md:col-span-7 md:row-span-2",
  },
  {
    key: "ai",
    eyebrow: "02 — Intelligence",
    title: "AI Agents & Workflows",
    body: "Reliable agentic systems, RAG pipelines and LLM-powered internal tools — evaluated, monitored and tuned for real users.",
    tags: ["LLM Agents", "RAG", "Eval & Tuning", "Tool-use"],
    span: "md:col-span-5",
  },
  {
    key: "sec",
    eyebrow: "03 — Defence",
    title: "Cyber Security",
    body: "Threat modelling, pen-testing, SOC2-aligned hardening and incident response playbooks for fast-moving teams.",
    tags: ["Pen-testing", "SOC2", "DevSecOps", "Incident response"],
    span: "md:col-span-5",
  },
];

function ServiceCard({ s, idx }) {
  return (
    <motion.article
      data-testid={TID.services.card(s.key)}
      initial={{ y: 40, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ delay: idx * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative ${s.span} overflow-hidden rounded-2xl border border-hairline bg-glass backdrop-blur-2xl p-8 md:p-10 hover:border-hairline-strong transition-colors`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.06),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
      <div className="relative">
        <div className="flex items-center gap-3 mb-10">
          <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
            {s.eyebrow}
          </span>
          <span className="h-px flex-1 bg-hairline" />
        </div>
        <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.05] text-primary-x max-w-md">
          {s.title}
        </h3>
        <p className="mt-6 text-secondary-x text-base md:text-lg leading-relaxed max-w-md">
          {s.body}
        </p>
        <div className="mt-10 flex flex-wrap gap-2">
          {s.tags.map((t) => (
            <span
              key={t}
              className="text-xs px-3 py-1.5 rounded-full border border-hairline-strong text-secondary-x bg-glass"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </motion.article>
  );
}

export default function Services() {
  const { scrollYProgress } = useScroll();
  const yBg = useTransform(scrollYProgress, [0.1, 0.5], [40, -60]);

  return (
    <section
      id="services"
      data-testid={TID.services.section}
      className="relative py-28 md:py-40 overflow-hidden"
    >
      <motion.div
        style={{ y: yBg }}
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.04),transparent_60%)]"
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
          <div>
            <span className="text-[11px] tracking-[0.28em] uppercase text-muted-x font-mono-x">
              ◇ Services
            </span>
            <h2 className="mt-4 font-display text-5xl md:text-7xl font-medium tracking-tight leading-[0.95] max-w-3xl text-primary-x">
              Three pillars. One studio.
            </h2>
          </div>
          <p className="md:max-w-sm text-secondary-x leading-relaxed">
            We focus deeply on the surfaces our clients actually care about —
            shipping production software, intelligent automation, and defensible
            security.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {SERVICES.map((s, i) => (
            <ServiceCard s={s} key={s.key} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
